import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import _debounce from 'lodash/debounce';
import React, { useState, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Paragraph, Spacer, TextButton } from '@zillow/constellation';
import { yieldCallback } from '@zillow/yield-callback';
import PropTypes from 'prop-types';
import { event } from '@zillow/universal-analytics';
import TextField from '../Fields/TextField.js';
import CondtionalField from '../Fields/ConditionalField.js';
import CheckboxField from '../Fields/CheckboxField.js';
import { FieldConfig } from './AffordabilityCalculator.utils.js';
import { commaSeparateNumber } from '../../utils/zhl-calculator.js';
import LegalPopover from '../CalculatorForm/LegalPopover.js';
import { trackLabel } from './trackLabel.js';

// Handle field change, debounced by 3 seconds.
var handleFieldChange = _debounce(function (label) {
  var cmsInfo = {
    block_type_cd: 'form_field',
    block_id: 'afffordability_calc',
    block_displayed_txt: label
  };
  sendInteractionEvent(cmsInfo);
}, 3000);
var sendInteractionEvent = function sendInteractionEvent(cmsInfo) {
  var newLaneData = {
    envelope: {
      event_type_id: '2056',
      event_type_version_id: '2',
      event_template_id: '126',
      event_template_version_id: '1'
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'cms',
      trigger_source_nm: 'cms_form',
      trigger_object_nm: 'no_trigger_object'
    },
    semantic: {
      semantic_event_nm: 'cms_engagement'
    },
    cms_info: cmsInfo
  };
  event(newLaneData);
};
function AffordabilityFields(_ref) {
  var className = _ref.className;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    advanced = _useState2[0],
    setAdvanced = _useState2[1];
  var form = useFormContext();
  form.register(FieldConfig.maximumPayment.name, FieldConfig.maximumPayment.config);

  // As we conditionally render content in here based on values restored from
  // local storage, we need to not render this component on the server,
  // otherwise we get React reconciliation errors.
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    loaded = _useState4[0],
    setLoaded = _useState4[1];
  useEffect(function () {
    setLoaded(true);
  }, []);
  if (!loaded) {
    return null;
  }
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, /*#__PURE__*/React.createElement(CondtionalField, {
    name: FieldConfig.customPayment.name,
    value: false
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Annual income",
    name: FieldConfig.annualIncome.name,
    leftAdornment: "$",
    config: FieldConfig.annualIncome.config,
    description: /*#__PURE__*/React.createElement(ToggleCustomPayment, null),
    autoComplete: "off",
    format: commaSeparateNumber,
    onBlur: function onBlur() {
      trackLabel('Annual Income Edit');
    },
    onChange: function onChange() {
      return handleFieldChange('Annual income');
    },
    tooltip: {
      title: 'Annual income',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Your total annual earnings before taxes, for you and your co-borrower. Annual income includes your base salary and any additional commissions, bonuses, tips, and investment income.")
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Monthly debts",
    name: FieldConfig.monthlyDebts.name,
    leftAdornment: "$",
    config: FieldConfig.monthlyDebts.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    onBlur: function onBlur() {
      trackLabel('Monthly Debts Edit');
    },
    onChange: function onChange() {
      return handleFieldChange('Monthly debts');
    }
  })), /*#__PURE__*/React.createElement(CondtionalField, {
    name: FieldConfig.customPayment.name,
    value: true
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Maximum payment",
    leftAdornment: "$",
    description: /*#__PURE__*/React.createElement(ToggleCustomPayment, null),
    name: FieldConfig.monthlyPayment.name,
    config: FieldConfig.monthlyPayment.config,
    autoComplete: "off",
    onBlur: function onBlur() {
      trackLabel('Maximum Payment Edit');
    },
    onChange: function onChange() {
      form.setValue(FieldConfig.maximumPayment.name, form.getValues(FieldConfig.monthlyPayment.name) * 2, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });
    },
    format: commaSeparateNumber
  })), /*#__PURE__*/React.createElement(TextField, {
    label: "Down payment",
    name: FieldConfig.downPayment.name,
    leftAdornment: "$",
    config: FieldConfig.downPayment.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    onBlur: function onBlur() {
      trackLabel('Down Payment Edit');
    },
    onChange: function onChange() {
      return handleFieldChange('Down payment');
    }
  }), /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "xs"
  }, /*#__PURE__*/React.createElement(TextButton, {
    type: "button",
    onClick: yieldCallback(function () {
      setAdvanced(!advanced);
      if (advanced) {
        trackLabel('Show Simple Calc');
      } else {
        trackLabel('Show Advanced Calc');
      }
    })
  }, advanced ? 'Simple' : 'Advanced')), advanced && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CondtionalField, {
    name: FieldConfig.customPayment.name,
    value: false
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Debt-to-income",
    name: FieldConfig.dti.name,
    rightAdornment: "%",
    config: FieldConfig.dti.config,
    autoComplete: "off",
    tooltip: {
      title: 'Debt-to-income (DTI) ratio',
      body: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Paragraph, null, "Enter the percentage of your income you are comfortable spending towards your mortgage. This number will also take monthly debts into account."), /*#__PURE__*/React.createElement(Paragraph, null, "Typically your DTI should be 36% or lower to qualify for a mortgage. Certain loans allow for higher DTIs, such as FHA loans (43%) and VA or USDA loans (41%)."))
    },
    onBlur: function onBlur() {
      trackLabel('Debt-to-income Edit');
    },
    onChange: function onChange() {
      return handleFieldChange('Debt-to-income');
    }
  })), /*#__PURE__*/React.createElement(TextField, {
    label: "Interest Rate",
    name: FieldConfig.rate.name,
    rightAdornment: "%",
    config: FieldConfig.rate.config,
    autoComplete: "off",
    tooltip: {
      title: 'Interest Rate',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Representative interest rates are based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. Close")
    },
    onBlur: function onBlur() {
      trackLabel('Rate Edit');
    },
    onChange: function onChange() {
      return handleFieldChange('Interest rate');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Loan term",
    name: FieldConfig.term.name,
    rightAdornment: "mos",
    config: FieldConfig.term.config,
    autoComplete: "off",
    tooltip: {
      title: 'Loan term',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "The loan term is the amount of time set by the lender for a buyer to pay off a mortgage. To convert a loan term to months from years, multiply the number of years by 12.")
    },
    onBlur: function onBlur() {
      trackLabel('Term Edit');
    },
    onChange: function onChange() {
      return handleFieldChange('Loan term');
    }
  }), /*#__PURE__*/React.createElement(CheckboxField, {
    label: "Include PMI",
    name: FieldConfig.includePMI.name,
    tooltip: {
      title: 'Private mortgage insurance',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Mortgage insurance is required primarily for borrowers on a conventional loan with a down payment of less than 20% of the home's purchase price. It protects lenders against some or most of the losses that can occur when a borrower defaults on a mortgage loan. Contact your lender for more details.")
    },
    onChange: function onChange(e) {
      trackLabel('Include PMI Edit');
      if (e.target.checked) {
        trackLabel('Include PMI Edit checked');
      } else {
        trackLabel('Include PMI Edit unchecked');
      }
    }
  }), /*#__PURE__*/React.createElement(CheckboxField, {
    label: "Include taxes/insurance",
    name: FieldConfig.includeTaxesInsurance.name,
    tooltip: {
      title: 'Taxes and insurance',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Includes property taxes, homeowners insurance, private mortgage insurance, and homeowners association dues.")
    },
    onChange: function onChange(e) {
      trackLabel('Include Taxes/Insurance Edit');
      if (e.target.checked) {
        trackLabel('Include Taxes/Insurance Edit checked');
      } else {
        trackLabel('Include Taxes/Insurance Edit unchecked');
      }
    }
  }), /*#__PURE__*/React.createElement(CondtionalField, {
    name: FieldConfig.includeTaxesInsurance.name,
    value: true
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Property tax",
    name: FieldConfig.propertyTaxRate.name,
    rightAdornment: "%",
    config: FieldConfig.propertyTaxRate.config,
    autoComplete: "off",
    tooltip: {
      title: 'Property tax',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "The mortgage payment calculator includes estimated property taxes. The value represents an annual tax on homeowner's property and the tax amount is based on the home's value.")
    },
    onBlur: function onBlur() {
      trackLabel('Annual Property Tax Rate Edit');
    },
    onChange: function onChange() {
      return handleFieldChange('Property tax');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Home insurance",
    name: FieldConfig.annualHomeownersInsurance.name,
    leftAdornment: "$",
    rightAdornment: "/yr",
    config: FieldConfig.annualHomeownersInsurance.config,
    format: commaSeparateNumber,
    autoComplete: "off",
    tooltip: {
      title: 'Home insurance',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Commonly known as hazard insurance, most lenders require insurance to provide damage protection for your home and personal property from a variety of events, including fire, lightning, burglary, vandalism, storms, explosions, and more. The personal liability coverage in standard homeowner's insurance policies is intended to protect against lawsuits involving injuries that occur on your property. Borrowers shop for their own home insurance. Close")
    },
    onBlur: function onBlur() {
      trackLabel('Annual Homeowners Insurance Edit');
    },
    onChange: function onChange() {
      return handleFieldChange('Home insurance');
    }
  })), /*#__PURE__*/React.createElement(TextField, {
    label: "HOA dues",
    name: FieldConfig.monthlyHOA.name,
    leftAdornment: "$",
    rightAdornment: "/mo",
    config: FieldConfig.monthlyHOA.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Homeowners association dues',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Typically, owners of condominiums or townhomes are required to pay homeowners association dues (known as HOA fees), to cover common amenities or services within the property such as garbage collection, landscaping, snow removal, pool maintenance, and hazard insurance.")
    },
    onBlur: function onBlur() {
      trackLabel('Monthly HOA Edit');
    },
    onChange: function onChange() {
      return handleFieldChange('HOA dues');
    }
  })), /*#__PURE__*/React.createElement(LegalPopover, {
    triggerText: "Calculator disclaimer",
    text: "The affordability calculator is intended to be used for educational purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. The estimates are based on information you provide, and may not include other fees and costs that a lender may assess in addition to monthly principal and interest, such as taxes and insurance and the actual payment obligation may be greater. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend."
  }));
}
AffordabilityFields.defaultProps = {
  className: undefined
};
AffordabilityFields.propTypes = process.env.NODE_ENV !== "production" ? {
  className: PropTypes.string
} : {};
function ToggleCustomPayment() {
  var form = useFormContext();
  var customPayment = useWatch({
    name: FieldConfig.customPayment.name
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, "Calculate by", ' ', /*#__PURE__*/React.createElement(TextButton, {
    type: "button",
    onClick: yieldCallback(function () {
      if (customPayment) {
        trackLabel('Calculate Method Click Income');
      } else {
        trackLabel('Calculate Method Click Payment');
      }
      form.setValue(FieldConfig.customPayment.name, !customPayment);
    })
    // This matches the font style of <FormHelp>
    ,
    fontType: "finePrint",
    tabIndex: -1
  }, customPayment ? 'income' : 'payment'));
}

export { AffordabilityFields as default };
